import React, {
  Dispatch, forwardRef, SetStateAction, useEffect, useImperativeHandle, useRef, useState,
} from 'react';
import {
  View, StyleSheet, Text, Pressable, FlatList, ScrollView,
} from 'react-native';
import ViewShot from 'react-native-view-shot';
import { FontAwesome } from '@expo/vector-icons';
import LocationSelector from '../../../components/Occupancy/LocationSelector';
import Style from '../../../constants/Style';
import TicketDetails from './TicketDetails';
import TicketsPerAsset from './TicketsPerAsset';
import { DateRange } from '../../../redux/dateRangeSlice';
import OverallTickets from './OverallTickets';
import PriorityStats from './PriorityStats';
import TimesOnTask from './TimesOnTask';
import SLADetails from './SLADetails';
import Colors from '../../../constants/Colors';
import SelectedTemplates from './SelectedTemplates';
import WidgetHeader from './WidgetHeader';
import Button from '../../../components/Button';
import EditIcon from '../../../components/EditIcon';
import isMobile from '../../../components/IsMobile';
import PressableStyled from '../../../components/PressableStyled';
import LocationSelectionModal from '../../InternalEnvironment/components/LocationSelectionModal';
import DownArrow24 from '../../../components/24pxIcons/DownArrow24';
import SLAViolationsGraph from './SLAViolationsGraph';
import DropdownMenu from '../../../components/DropdownMenu';
import getUserGroups from '../../../requests/Users/GetUserGroups';
import {
  Template, TemplateGroup, TicketPriority, UserGroup,
} from '../../../constants/Types';
import { logEvent } from '../../../components/LogEvent';
import { SolveAnalyticsWidget } from '../Utils';
import { Building } from '../../LocationManagement/components/AddBuildingModal';
import GetTicketsRaisedCounts from '../../../requests/SolveAnalytics/GetTicketsRaisedCounts';
import GetPriorityStats from '../../../requests/SolveAnalytics/GetPriorityStats';
import GetAssetTicketStats from '../../../requests/SolveAnalytics/GetAssetTicketStats';
import GetTimesOnTaskStats from '../../../requests/SolveAnalytics/GetTimesOnTaskStats';
import { getPreviousDateRange } from '../../../constants/Utils';
import GetSLAViolations from '../../../requests/SolveAnalytics/GetSLAViolations';

interface AnalyticsWidgetsProps {
  range: DateRange,
  compareRange: DateRange,
  selectedBuilding: Building,
  editTemplateSelection: () => void,
  renderHeader,
  templateGroups: TemplateGroup[],
  templates: Template[],
  selectedTemplateIds: string[] | null,
  selectedTemplate: Template,
  setSelectedTemplate: Dispatch<SetStateAction<Template>>,
  selectedTemplateGroup: TemplateGroup,
  setSelectedTemplateGroup: Dispatch<SetStateAction<TemplateGroup>>,
  selectedUserGroup: UserGroup,
  setSelectedUserGroup: Dispatch<SetStateAction<UserGroup>>,
}

const AnalyticsWidgets = forwardRef(({
  range, compareRange, selectedBuilding, templateGroups, templates, selectedTemplateIds,
  editTemplateSelection, renderHeader, selectedTemplate, setSelectedTemplate,
  selectedTemplateGroup, setSelectedTemplateGroup, selectedUserGroup, setSelectedUserGroup,
}: AnalyticsWidgetsProps, ref) => {
  const [overallTicketsLoading, setOverallTicketsLoading] = useState<boolean>(false);
  const [overallTicketsComparisonLoading,
    setOverallTicketsComparisonLoading] = useState<boolean>(false);
  const [priorityStatsLoading, setPriorityStatsLoading] = useState<boolean>(false);
  const [ticketsPerAssetLoading, setTicketsPerAssetLoading] = useState<boolean>(false);
  const [timesOnTaskLoading, setTimesOnTaskLoading] = useState<boolean>(false);
  const [timesOnTaskComparisonLoading, setTimesOnTaskComparisonLoading] = useState<boolean>(false);
  const [slaViolationsGraphLoading, setSLAViolationsGraphLoading] = useState<boolean>(false);
  const [overallTicketsData, setOverallTicketsData] = useState(null);
  const [overallTicketsComparisonData, setOverallTicketsComparisonData] = useState(null);
  const [priorityStatisticsData, setPriorityStatisticsData] = useState(null);
  const [priorityStatisticsComparisonData, setPriorityStatisticsComparisonData] = useState(null);
  const [ticketsPerAssetData, setTicketsPerAssetData] = useState(null);
  const [ticketsPerAssetComparisonData, setTicketsPerAssetComparisonData] = useState(null);
  const [slaViolationsData, setSLAViolationsData] = useState(null);
  const [slaViolationsComparisonData, setSLAViolationsComparisonData] = useState(null);
  const [timesOnTaskStatistics, setTimesOnTaskStatistics] = useState(null);
  const [timesOnTaskComparisonStatistics, setTimesOnTaskComparisonStatistics] = useState(null);
  const [selectedFloorId, setSelectedFloorId] = useState<string | null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  const [locationSelectorVisible, setLocationSelectorVisible] = useState<boolean>(false);
  const [locationName, setLocationName] = useState<string | null>(selectedBuilding?.Name);
  const [userGroupDropdownVisible, setUserGroupDropdownVisible] = useState<boolean>(false);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [templateGroupDropdownVisible, setTemplateGroupDropdownVisible] = useState<boolean>(false);
  const [templateDropdownVisible, setTemplateDropdownVisible] = useState<boolean>(false);
  const [timeOnTaskPriority, setTimeOnTaskPriority] = useState<TicketPriority | null>(null);
  const [scrollIndex, setScrollIndex] = useState<number>(0);
  const flatListRef = useRef<FlatList>(null);
  const selectedBuildingId: string = selectedBuilding?.LocationId;
  const buildingId: string = selectedBuilding?.LocationId;
  const templateGroupId: string = selectedTemplateGroup?.ButtonPrototypeGroupId;
  const templateId: string = selectedTemplate?.id;
  const userGroupId: string = selectedUserGroup?.UserGroupId;
  const ticketsPerTemplateViewShotRef = useRef<ViewShot>(null);
  const overallTicketsViewShotRef = useRef<ViewShot>(null);
  const priorityStatisticsViewShotRef = useRef<ViewShot>(null);
  const ticketsPerAssetViewShotRef = useRef<ViewShot>(null);
  const timesOnTaskViewShotRef = useRef<ViewShot>(null);
  const ticketDetailsViewShotRef = useRef<ViewShot>(null);
  const slaTargetsViewShotRef = useRef<ViewShot>(null);
  const slaViolationsByAssetViewShotRef = useRef<ViewShot>(null);
  const slaViolationsByLocationViewShotRef = useRef<ViewShot>(null);
  const mostFrequentTicketsRaisedViewShotRef = useRef<ViewShot>(null);
  const slaViolationsByDateViewShotRef = useRef<ViewShot>(null);

  const compareRangeExists: boolean = !!compareRange.start && !!compareRange.end;
  const selectedTemplates: Template[] = templates?.length > 0
    ? templates.filter((template: Template) => selectedTemplateIds?.includes(template.id)) : null;

  useImperativeHandle(ref, () => ({
    capture: async () => {
      const images = [];
      images.push({
        name: SolveAnalyticsWidget.TICKETS_RAISED_BY_TEMPLATE,
        uri: await ticketsPerTemplateViewShotRef.current?.capture(),
      });
      images.push({
        name: SolveAnalyticsWidget.OVERALL_TICKETS_RAISED,
        uri: await overallTicketsViewShotRef.current?.capture(),
      });
      images.push({
        name: SolveAnalyticsWidget.PRIORITY_STATISTICS,
        uri: await priorityStatisticsViewShotRef.current?.capture(),
      });
      images.push({
        name: SolveAnalyticsWidget.TICKETS_RAISED_PER_ASSET,
        uri: await ticketsPerAssetViewShotRef.current?.capture(),
      });
      images.push({
        name: SolveAnalyticsWidget.TIME_ON_TASK,
        uri: await timesOnTaskViewShotRef.current?.capture(),
      });
      images.push({
        name: SolveAnalyticsWidget.TICKET_DETAILS,
        uri: await ticketDetailsViewShotRef.current?.capture(),
      });
      images.push({
        name: SolveAnalyticsWidget.SLA_TARGETS,
        uri: await slaTargetsViewShotRef.current?.capture(),
      });
      images.push({
        name: SolveAnalyticsWidget.SLA_VIOLATIONS_BY_ASSET,
        uri: await slaViolationsByAssetViewShotRef.current?.capture(),
      });
      images.push({
        name: SolveAnalyticsWidget.SLA_VIOLATIONS_BY_LOCATION,
        uri: await slaViolationsByLocationViewShotRef.current?.capture(),
      });
      images.push({
        name: SolveAnalyticsWidget.MOST_FREQUENT_TICKETS_RAISED,
        uri: await mostFrequentTicketsRaisedViewShotRef.current?.capture(),
      });
      images.push({
        name: SolveAnalyticsWidget.SLA_VIOLATIONS_BY_DATE,
        uri: await slaViolationsByDateViewShotRef.current?.capture(),
      });
      return images;
    },
  }));

  useEffect(() => {
    getUserGroups().then((response: { UserGroups?: UserGroup[] }) => {
      setUserGroups(response?.UserGroups?.sort((a: UserGroup, b: UserGroup) => a.Name?.toLowerCase()
        .localeCompare(b.Name?.toLowerCase())) ?? []);
    });
  }, []);

  useEffect(() => {
    setSelectedFloorId(null);
    setSelectedAreaId(null);
    setSelectedSpaceId(null);
  }, [buildingId]);

  useEffect(() => {
    if (!templateDropdownVisible) return;
    setTemplateGroupDropdownVisible(false);
    setUserGroupDropdownVisible(false);
  }, [templateDropdownVisible]);

  useEffect(() => {
    if (!templateGroupDropdownVisible) return;
    setTemplateDropdownVisible(false);
    setUserGroupDropdownVisible(false);
  }, [templateGroupDropdownVisible]);

  useEffect(() => {
    if (!userGroupDropdownVisible) return;
    setTemplateGroupDropdownVisible(false);
    setTemplateDropdownVisible(false);
  }, [userGroupDropdownVisible]);

  useEffect(() => {
    if (!(selectedTemplateGroup && selectedTemplate)) return;
    if (!selectedTemplateGroup?.buttonPrototypes
      .some((template: Template) => template.id === selectedTemplate.id)) setSelectedTemplate(null);
  }, [selectedTemplateGroup]);

  useEffect(() => {
    if (range.start && range.end && buildingId) {
      setOverallTicketsLoading(true);
      GetTicketsRaisedCounts({
        range,
        templateGroupId,
        templateId,
        userGroupId,
        buildingId,
        floorId: selectedFloorId,
        areaId: selectedAreaId,
        spaceId: selectedSpaceId,
      }).then((result) => {
        setOverallTicketsData(result?.ticketsRaisedCounts ?? []);
      }).finally(() => setOverallTicketsLoading(false));

      setPriorityStatsLoading(true);
      GetPriorityStats({
        range,
        compareRange,
        templateGroupId,
        templateId,
        userGroupId,
        buildingId,
        floorId: selectedFloorId,
        areaId: selectedAreaId,
        spaceId: selectedSpaceId,
      }).then((result) => {
        setPriorityStatisticsData(result?.priorityStatistics);
        setPriorityStatisticsComparisonData(result?.comparisonPriorityStatistics);
      }).finally(() => setPriorityStatsLoading(false));

      setTicketsPerAssetLoading(true);
      GetAssetTicketStats({
        range,
        compareRange,
        buildingId,
        floorId: selectedFloorId,
        areaId: selectedAreaId,
        spaceId: selectedSpaceId,
        templateGroupId,
        templateId,
        userGroupId,
      }).then((result) => {
        const ticketsData = result?.ticketsData
          ?.sort((prev, next) => prev.assetName.localeCompare(next.assetName))
          ?.sort((prev, next) => next.count - prev.count).slice(0, 20) ?? [];
        setTicketsPerAssetData(ticketsData);
        setTicketsPerAssetComparisonData(ticketsData?.map((item) => ({
          ...item,
          count: result?.comparisonTicketData
            ?.filter((comparisonItem) => comparisonItem.assetId === item.assetId)[0]?.count ?? 0,
        })) ?? []);
      }).finally(() => setTicketsPerAssetLoading(false));

      setSLAViolationsGraphLoading(true);
      GetSLAViolations({
        range,
        compareRange,
        templateId,
        templateGroupId,
        userGroupId,
        buildingId,
        floorId: selectedFloorId,
        areaId: selectedAreaId,
        spaceId: selectedSpaceId,
      }).then((result) => {
        setSLAViolationsData((result?.SLAViolations ?? [])
          .map(({ key, value }, index) => ({ value, date: key, index })));
        setSLAViolationsComparisonData((result?.comparisonSLAViolationCount ?? [])
          .map(({ key, value }, index) => ({ value, date: key, index })));
      }).finally(() => setSLAViolationsGraphLoading(false));
    }

    if (compareRangeExists) {
      setOverallTicketsComparisonLoading(true);
      GetTicketsRaisedCounts({
        range: compareRange,
        templateGroupId,
        templateId,
        userGroupId,
        buildingId,
        floorId: selectedFloorId,
        areaId: selectedAreaId,
        spaceId: selectedSpaceId,
      }).then((result) => {
        setOverallTicketsComparisonData(result?.ticketsRaisedCounts ?? []);
      }).finally(() => setOverallTicketsComparisonLoading(false));
    } else {
      setOverallTicketsComparisonData(null);
    }
  }, [range, compareRange, templateGroupId, templateId, userGroupId, buildingId, selectedFloorId,
    selectedAreaId, selectedSpaceId]);

  useEffect(() => {
    setTimesOnTaskLoading(true);
    GetTimesOnTaskStats({
      range,
      compareRange: compareRangeExists ? compareRange : getPreviousDateRange(range),
      buildingId,
      templateId,
      templateGroupId,
      userGroupId,
      floorId: selectedFloorId,
      areaId: selectedAreaId,
      spaceId: selectedSpaceId,
      priority: timeOnTaskPriority,
    }).then(setTimesOnTaskStatistics).finally(() => setTimesOnTaskLoading(false));

    if (compareRangeExists) {
      setTimesOnTaskComparisonLoading(true);
      GetTimesOnTaskStats({
        range: compareRange,
        buildingId,
        templateId,
        templateGroupId,
        userGroupId,
        floorId: selectedFloorId,
        areaId: selectedAreaId,
        spaceId: selectedSpaceId,
        priority: timeOnTaskPriority,
      }).then(setTimesOnTaskComparisonStatistics)
        .finally(() => setTimesOnTaskComparisonLoading(false));
    } else {
      setTimesOnTaskComparisonStatistics(null);
    }
  }, [range, compareRange, templateGroupId, templateId, userGroupId, buildingId, selectedFloorId,
    selectedAreaId, selectedSpaceId, timeOnTaskPriority]);

  const resetFilters: () => void = () => {
    logEvent('SolveAnalytics', 'Reset_Buttons_Pressed_Graph');
    setTemplateDropdownVisible(false);
    setTemplateGroupDropdownVisible(false);
    setUserGroupDropdownVisible(false);
    setSelectedTemplateGroup(null);
    setSelectedTemplate(null);
    setSelectedUserGroup(null);
  };

  const commonProps = {
    range,
    compareRange,
    templateGroupId,
    templateId,
    userGroupId,
    buildingId,
    selectedFloorId,
    selectedAreaId,
    selectedSpaceId,
  };

  return <View style={!isMobile && styles.containerWeb}>
    {!isMobile ? <View style={styles.locationSelectorWeb}>
      <LocationSelector
        hideOtherBuildings
        {...{
          selectedBuildingId,
          selectedFloorId,
          setSelectedFloorId,
          selectedAreaId,
          setSelectedAreaId,
          selectedSpaceId,
          setSelectedSpaceId,
          setLocationName,
        }}
      />
    </View> : <>
      <Pressable
        onPress={() => setLocationSelectorVisible(true)}
        style={styles.locationSelectorMobile}
      >
        <Text style={styles.locationName}>{locationName ?? selectedBuilding?.Name}</Text>
        <DownArrow24 style={locationSelectorVisible ? Style.inverted : Style.upright} />
      </Pressable>
      {renderHeader()}
    </>}
    <ScrollView
      style={styles.content}
      contentContainerStyle={isMobile && { paddingBottom: 180 }}
      nestedScrollEnabled
    >
      {!isMobile && <Text style={styles.buildingName}>{locationName}</Text>}
      <View style={[Style.row, { zIndex: 2 }]}>
        <WidgetHeader
          title='Tickets raised by template'
          {...{ range, compareRange }}
          tooltipText='Shown below are the number of tickets raised against assets belonging to the selection of templates and date range'
        />
        {!isMobile && <Button
          title='Edit'
          children={<EditIcon />}
          style={styles.editButton}
          onPress={editTemplateSelection}
          testID='edit-template-cards-btn'
        />}
      </View>
      <SelectedTemplates
        {...{
          selectedTemplates, range, compareRange, flatListRef, buildingId,
        }}
        viewShotRef={ticketsPerTemplateViewShotRef}
      />
      {isMobile && <View style={Style.row}>
        <Button
          title='Edit'
          children={<EditIcon />}
          style={styles.editButton}
          onPress={editTemplateSelection}
          testID='edit-template-cards-btn'
        />
        <View style={Style.row}>
          <PressableStyled
            style={styles.scrollButtons}
            onLongPress={() => {
              setScrollIndex(0);
              flatListRef?.current?.scrollToIndex({ index: 0 });
            }}
            disabled={!(scrollIndex > 1)}
            onPress={() => {
              const index: number = Math.max(scrollIndex - 2, 0);
              setScrollIndex(index);
              flatListRef?.current?.scrollToIndex({ index, viewPosition: 0.5 });
            }}
          >
            <FontAwesome
              name='angle-left'
              size={24}
              color={Colors.light.hereworksBlue500}
            />
          </PressableStyled>
          <PressableStyled
            style={styles.scrollButtons}
            onLongPress={() => {
              setScrollIndex((selectedTemplates?.length ?? 1 - 1));
              flatListRef?.current?.scrollToEnd();
            }}
            disabled={!(selectedTemplates?.length > 2)}
            onPress={() => {
              const index: number = Math.min(
                scrollIndex + 2,
                (selectedTemplates?.length ?? 1) - 1,
              );
              setScrollIndex(index);
              flatListRef?.current?.scrollToIndex({ index, viewPosition: 0.5 });
            }}
          >
            <FontAwesome
              name='angle-right'
              size={24}
              color={Colors.light.hereworksBlue500}
            />
          </PressableStyled>
        </View>
      </View>}
      <View style={[styles.dropdownsSection, { zIndex: 2 }]}>
        <View style={[styles.dropdownContainer, { zIndex: 2 }]}>
          <DropdownMenu
            expanded={templateGroupDropdownVisible}
            setExpanded={setTemplateGroupDropdownVisible}
            options={templateGroups}
            setSelectedOption={setSelectedTemplateGroup}
            selectedOption={selectedTemplateGroup}
            style={[styles.dropdown, { zIndex: 2 }]}
            title='Template group'
            buttonTestID='select-template-group-btn'
            listTestID='template-group-list'
          />
          <DropdownMenu
            expanded={templateDropdownVisible}
            setExpanded={setTemplateDropdownVisible}
            options={selectedTemplateGroup?.buttonPrototypes ?? templates}
            setSelectedOption={setSelectedTemplate}
            selectedOption={selectedTemplate}
            style={[styles.dropdown, styles.templateDropdown]}
            title='Template'
            buttonTestID='select-template-btn'
            listTestID='template-list'
          />
          {!isMobile && (selectedTemplateGroup || selectedTemplate || selectedUserGroup)
            && <PressableStyled
              style={styles.resetButton}
              onPress={resetFilters}
              testID='reset-button'
            >
              <Text style={styles.resetText}>{'Reset'}</Text>
              <View
                style={{ width: 39, height: 1, backgroundColor: Colors.light.hereworksPrimaryGrey }}
              />
            </PressableStyled>}
        </View>
        <DropdownMenu
          expanded={userGroupDropdownVisible}
          setExpanded={setUserGroupDropdownVisible}
          options={userGroups}
          setSelectedOption={setSelectedUserGroup}
          selectedOption={selectedUserGroup}
          style={styles.dropdown}
          title='User group'
          buttonTestID='select-user-group-btn'
          listTestID='user-group-list'
        />
        {isMobile && (selectedTemplateGroup || selectedTemplate || selectedUserGroup)
          && <PressableStyled
            style={styles.resetButton}
            onPress={resetFilters}
            testID='reset-button'
          >
            <Text style={styles.resetText}>{'Reset'}</Text>
            <View
              style={{ width: 39, height: 1, backgroundColor: Colors.light.hereworksPrimaryGrey }}
            />
          </PressableStyled>}
      </View>
      <OverallTickets
        {...commonProps}
        viewShotRef={overallTicketsViewShotRef}
        loading={overallTicketsLoading || overallTicketsComparisonLoading}
        data={overallTicketsData}
        comparisonData={overallTicketsComparisonData}
      />
      <PriorityStats
        {...commonProps}
        viewShotRef={priorityStatisticsViewShotRef}
        loading={priorityStatsLoading}
        data={priorityStatisticsData}
        comparisonData={priorityStatisticsComparisonData}
      />
      <TicketsPerAsset
        {...commonProps}
        viewShotRef={ticketsPerAssetViewShotRef}
        loading={ticketsPerAssetLoading}
        data={ticketsPerAssetData}
        comparisonData={ticketsPerAssetComparisonData}
      />
      <View style={{ zIndex: 4 }}>
        <TimesOnTask
          {...commonProps}
          selectedTicketPriority={timeOnTaskPriority}
          setSelectedTicketPriority={setTimeOnTaskPriority}
          viewShotRef={timesOnTaskViewShotRef}
          loading={timesOnTaskLoading || timesOnTaskComparisonLoading}
          data={timesOnTaskStatistics}
          comparisonData={timesOnTaskComparisonStatistics}
        />
      </View>
      <View style={{ zIndex: 3 }}>
        <TicketDetails
          {...commonProps}
          viewShotRef={ticketDetailsViewShotRef}
        />
      </View>
      <SLADetails
        {...commonProps}
        {...{
          slaTargetsViewShotRef,
          slaViolationsByAssetViewShotRef,
          slaViolationsByLocationViewShotRef,
          mostFrequentTicketsRaisedViewShotRef,
        }}
      />
      <SLAViolationsGraph
        {...commonProps}
        viewShotRef={slaViolationsByDateViewShotRef}
        loading={slaViolationsGraphLoading}
        data={slaViolationsData}
        comparisonData={slaViolationsComparisonData}
      />
    </ScrollView>
    <LocationSelectionModal
      isVisible={locationSelectorVisible}
      hide={() => setLocationSelectorVisible(false)}
      {...{
        setLocationName, setSelectedFloorId, setSelectedAreaId, setSelectedSpaceId,
      }}
    />
  </View>;
});

const styles = StyleSheet.create({
  containerWeb: {
    ...Style.row,
    flex: 1,
    backgroundColor: '#FFF',
    marginTop: 12,
    borderRadius: 24,
    justifyContent: 'flex-start',
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite300,
  },
  locationSelectorMobile: {
    ...Style.row,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksWhite500,
    backgroundColor: '#FFF',
    paddingHorizontal: 12,
    paddingVertical: 9,
    height: 48,
  },
  locationName: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
    marginEnd: 8,
    flex: 1,
  },
  locationSelectorWeb: {
    width: 240,
    alignSelf: 'stretch',
    borderEndWidth: 1,
    borderEndColor: Colors.light.hereworksBlue100,
  },
  content: {
    paddingTop: 24,
    paddingStart: isMobile ? undefined : 24,
    paddingEnd: 20,
    marginEnd: isMobile ? -20 : 4,
    height: '100%',
  },
  buildingName: {
    fontSize: 22,
    lineHeight: 22,
    letterSpacing: 0,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    marginBottom: 24,
  },
  editButton: {
    ...Style.row,
    width: 72,
    flex: undefined,
  },
  scrollButtons: {
    width: 32,
    aspectRatio: 1,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    alignItems: 'center',
    justifyContent: 'center',
    marginStart: 8,
  },
  dropdownsSection: {
    marginTop: 24,
    marginBottom: 16,
  },
  dropdownContainer: {
    alignSelf: 'stretch',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: isMobile ? 'stretch' : 'flex-start',
  },
  dropdown: {
    width: isMobile ? undefined : 240,
  },
  templateDropdown: {
    marginStart: isMobile ? undefined : 8,
  },
  resetText: {
    color: Colors.light.hereworksPrimaryGrey,
    fontSize: 14,
    fontFamily: 'Poppins_400Regular',
    paddingBottom: 2,
  },
  resetButton: {
    marginStart: isMobile ? 0 : 16,
    marginTop: 4,
    justifyContent: 'center',
    zIndex: -1,
  },
});

export default AnalyticsWidgets;
